import {createSmoker} from './smoker.js';
import {createSmokerCapabilities} from './smoker-capabilities.js';

function generatedId(value){
  const id=String(value||'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function errorMessage(error){
  return String(error?.message||'Invalid value').slice(0,180);
}

function classifySmokerError(message){
  const text=String(message||'');
  if(/name/i.test(text))return 'name';
  if(/smokerTypeKey/i.test(text))return 'smokerTypeKey';
  if(/notes/i.test(text))return 'notes';
  if(/sortOrder/i.test(text))return 'sortOrder';
  return 'smoker';
}

function classifyCapabilityError(message){
  const text=String(message||'');
  if(/fuel/i.test(text))return 'fuelTypes';
  if(/minTemperature/i.test(text))return 'minTemperatureCelsius';
  if(/maxTemperature/i.test(text))return 'maxTemperatureCelsius';
  if(/coldSmokeMax/i.test(text))return 'coldSmokeMaxCelsius';
  if(/coldSmoking/i.test(text))return 'coldSmoking';
  if(/temperatureControl/i.test(text))return 'temperatureControl';
  if(/controlPrecision/i.test(text))return 'controlPrecisionCelsius';
  return 'capabilities';
}

export function validateCreateSmokerForm({
  id,
  userId,
  name,
  smokerTypeKey=null,
  notes=null,
  sortOrder=0,
  isDefault=false,
  fuelTypes=[],
  minTemperatureCelsius=null,
  maxTemperatureCelsius=null,
  coldSmoking='unknown',
  coldSmokeMaxCelsius=null,
  temperatureControl='unknown',
  controlPrecisionCelsius=null
}={}){
  const smokerId=generatedId(id);
  const errors={};
  let smoker=null;
  let capabilities=null;

  try{
    smoker=createSmoker({
      id:smokerId,
      userId,
      name,
      smokerTypeKey,
      notes,
      sortOrder,
      isDefault
    });
  }catch(error){
    errors[classifySmokerError(errorMessage(error))]=errorMessage(error);
  }

  try{
    capabilities=createSmokerCapabilities({
      smokerId,
      fuelTypes,
      minTemperatureCelsius,
      maxTemperatureCelsius,
      coldSmoking,
      coldSmokeMaxCelsius,
      temperatureControl,
      controlPrecisionCelsius
    });
  }catch(error){
    errors[classifyCapabilityError(errorMessage(error))]=errorMessage(error);
  }

  const ok=Object.keys(errors).length===0;
  return Object.freeze({
    ok,
    smokerId,
    errors:Object.freeze(errors),
    normalized:ok?Object.freeze({smoker,capabilities}):null
  });
}

export function createSmokerFlow({
  smokerStore,
  capabilityStore,
  isOnline=()=>globalThis.navigator?.onLine!==false
}={}){
  if(typeof smokerStore?.create!=='function')throw new TypeError('smokerStore.create is required');
  if(typeof capabilityStore?.save!=='function')throw new TypeError('capabilityStore.save is required');

  async function save(input={}, {id=null}={}){
    const validation=validateCreateSmokerForm(id?{...input,id}:input);
    if(!validation.ok){
      return Object.freeze({
        ok:false,
        status:'validation_error',
        smokerId:validation.smokerId,
        errors:validation.errors,
        retryable:false
      });
    }

    let savedSmoker;
    try{
      savedSmoker=id
        ?await smokerStore.edit(id,{
          name:validation.normalized.smoker.name,
          smokerTypeKey:validation.normalized.smoker.smokerTypeKey,
          notes:validation.normalized.smoker.notes,
          sortOrder:validation.normalized.smoker.sortOrder,
          photoPath:validation.normalized.smoker.photoPath
        })
        :await smokerStore.create(validation.normalized.smoker);
    }catch(error){
      return Object.freeze({
        ok:false,
        status:'save_failed',
        smokerId:validation.smokerId,
        errorCode:String(error?.code||error?.name||'SMOKER_SAVE_FAILED').slice(0,80),
        retryable:true
      });
    }

    try{
      await capabilityStore.save(
        validation.smokerId,
        validation.normalized.capabilities
      );
    }catch(error){
      return Object.freeze({
        ok:false,
        status:'partial_save',
        smokerId:validation.smokerId,
        savedSmoker,
        errorCode:String(error?.code||error?.name||'CAPABILITY_SAVE_FAILED').slice(0,80),
        retryable:true
      });
    }

    return Object.freeze({
      ok:true,
      status:isOnline()?'saved_pending_sync':'saved_offline',
      mode:id?'updated':'created',
      smokerId:validation.smokerId,
      savedSmoker,
      retryable:false
    });
  }

  async function load(id){
    const smoker=await smokerStore.get(id);
    if(!smoker)throw new TypeError('Smoker not found');
    const capabilities=await capabilityStore.get(id);
    return Object.freeze({
      ...smoker,
      ...(capabilities?{
        fuelTypes:capabilities.fuelTypes,
        minTemperatureCelsius:capabilities.minTemperatureCelsius,
        maxTemperatureCelsius:capabilities.maxTemperatureCelsius,
        coldSmoking:capabilities.coldSmoking,
        coldSmokeMaxCelsius:capabilities.coldSmokeMaxCelsius,
        temperatureControl:capabilities.temperatureControl,
        controlPrecisionCelsius:capabilities.controlPrecisionCelsius
      }:{})
    });
  }

  return Object.freeze({save,load,validate:validateCreateSmokerForm});
}
